import type { components } from "../../shared/api/generated/trading-engine.js";
import { usdToMinorUnits } from "../../shared/lib/usd.js";
import type { BotConfiguration } from "./models.js";

type BotMutation = components["schemas"]["BotMutation"];

export interface BotFormValues {
  readonly allowShort: boolean;
  readonly defaultOrderType: "limit" | "market";
  readonly exchange: "XNAS" | "XNYS";
  readonly liveBrokerAccountId: string;
  readonly maxPositionNotionalUsd: string;
  readonly maxPositionQuantity: string;
  readonly name: string;
  readonly parametersJson: string;
  readonly positionSizingKind: "fixed-notional" | "fixed-quantity" | "percent-of-equity";
  readonly positionSizingValue: string;
  readonly regularSessionOnly: boolean;
  readonly strategyKind: string;
  readonly strategyVersion: string;
  readonly symbol: string;
  readonly timeInForce: "day" | "gtc";
}

export function valuesFromConfiguration(
  configuration: BotConfiguration,
  name = "",
  liveBrokerAccountId = "",
): BotFormValues {
  return {
    allowShort: configuration.risk.allowShort,
    defaultOrderType: configuration.execution.defaultOrderType,
    exchange: configuration.schedule.exchange,
    liveBrokerAccountId,
    maxPositionNotionalUsd: configuration.risk.maxPositionNotionalUsd,
    maxPositionQuantity: configuration.risk.maxPositionQuantity,
    name,
    parametersJson: JSON.stringify(configuration.strategy.parameters, null, 2),
    positionSizingKind: configuration.positionSizing.kind,
    positionSizingValue: configuration.positionSizing.value,
    regularSessionOnly: configuration.schedule.regularSessionOnly,
    strategyKind: configuration.strategy.kind,
    strategyVersion: configuration.strategy.version,
    symbol: configuration.symbol,
    timeInForce: configuration.execution.timeInForce,
  };
}

export function botMutation(values: BotFormValues, preserveEmptyAccount = true): BotMutation {
  const maxPositionQuantity = values.maxPositionQuantity.trim();
  const maxPositionNotionalUsd = values.maxPositionNotionalUsd.trim();
  return {
    configuration: {
      execution: {
        defaultOrderType: values.defaultOrderType,
        timeInForce: values.timeInForce,
      },
      positionSizing: {
        kind: values.positionSizingKind,
        value: values.positionSizingValue.trim(),
      },
      risk: {
        allowShort: values.allowShort,
        ...(maxPositionQuantity === "" ? {} : { maxPositionQuantity }),
        ...(maxPositionNotionalUsd === ""
          ? {}
          : {
              maxPositionNotional: {
                currency: "USD",
                minorUnits: usdToMinorUnits(maxPositionNotionalUsd),
              },
            }),
      },
      schedule: {
        exchange: values.exchange,
        regularSessionOnly: values.regularSessionOnly,
        timeZone: "America/New_York",
      },
      strategy: {
        kind: values.strategyKind.trim(),
        parameters: JSON.parse(values.parametersJson) as unknown,
        version: Number(values.strategyVersion),
      },
      symbol: values.symbol.trim().toUpperCase(),
    },
    ...(values.liveBrokerAccountId === ""
      ? preserveEmptyAccount
        ? {}
        : { liveBrokerAccountId: null }
      : { liveBrokerAccountId: values.liveBrokerAccountId }),
    name: values.name.trim(),
  };
}
